import { format } from 'date-fns';
import { imageSpaceAPI } from '@/utils/api';
import { getImageFromIndexedDB } from '@/utils/imageStorage';
import { resolveImageDisplayByteSize } from './imageSpaceStorageStats';
import { resolveImageSpaceDisplayRawUrl } from './imageSpaceDisplayUrl';

function trimmed(v) {
  return typeof v === 'string' ? v.trim() : '';
}

function formatServerTime(raw) {
  if (raw == null || raw === '') return '';
  if (raw instanceof Date) {
    return !Number.isNaN(raw.getTime()) ? format(raw, 'yyyy-MM-dd HH:mm') : '';
  }
  if (typeof raw === 'string') {
    if (raw.includes('T') && (raw.includes('Z') || raw.includes('+'))) {
      try {
        const d = new Date(raw);
        return !Number.isNaN(d.getTime()) ? format(d, 'yyyy-MM-dd HH:mm') : raw;
      } catch {
        return raw;
      }
    }
    return raw;
  }
  try {
    const d = new Date(raw);
    return !Number.isNaN(d.getTime()) ? format(d, 'yyyy-MM-dd HH:mm') : '';
  } catch {
    return '';
  }
}

function formatCreatedAt(image) {
  if (!image?.createdAt) return '';
  const raw = image.createdAt;
  if (typeof raw === 'string' && raw.includes('T') && (raw.includes('Z') || raw.includes('+'))) {
    try {
      const date = new Date(raw);
      return !Number.isNaN(date.getTime()) ? format(date, 'yyyy-MM-dd HH:mm') : raw;
    } catch {
      return raw;
    }
  }
  return raw;
}

/**
 * 合并本地/IndexedDB 与服务端元数据，供图片空间预览弹窗使用。
 * @param {Record<string, unknown>} image
 * @param {{ resolveUrl?: (id: string, row: Record<string, unknown>) => string | Promise<string> }} [options]
 */
export async function buildImageSpacePreviewImage(image, { resolveUrl } = {}) {
  if (!image?.id) return null;

  let formattedCreatedAt = formatCreatedAt(image);

  let effectiveSrc = resolveImageSpaceDisplayRawUrl(image);

  if (!effectiveSrc && typeof resolveUrl === 'function') {
    const resolved = await Promise.resolve(resolveUrl(image.id, image));
    if (resolved) effectiveSrc = trimmed(resolved);
  }

  if (!effectiveSrc) {
    try {
      const local = await getImageFromIndexedDB(image.id);
      if (local) effectiveSrc = local;
    } catch (error) {
      console.warn(`无法加载预览图片 ${image.id}:`, error);
    }
  }

  let imageWithUrl = {
    ...image,
    name: image.name || '',
    createdAt: formattedCreatedAt,
    width: image.width || null,
    height: image.height || null,
    format: image.format || '',
    size: resolveImageDisplayByteSize(image, effectiveSrc),
    src: effectiveSrc,
  };

  try {
    const res = await imageSpaceAPI.getById(String(image.id));
    const s = res?.image ?? res?.data;
    if (s && typeof s === 'object') {
      const sw = s.width != null && s.width !== '' ? Number(s.width) : null;
      const sh = s.height != null && s.height !== '' ? Number(s.height) : null;
      const serverCreated = formatServerTime(s.createdAt ?? s.uploadedAt);
      const lw =
        imageWithUrl.width != null && imageWithUrl.width !== ''
          ? Number(imageWithUrl.width)
          : null;
      const lh =
        imageWithUrl.height != null && imageWithUrl.height !== ''
          ? Number(imageWithUrl.height)
          : null;
      imageWithUrl = {
        ...imageWithUrl,
        name: (imageWithUrl.name && String(imageWithUrl.name).trim()) || (typeof s.name === 'string' ? s.name : '') || '',
        width: lw != null && !Number.isNaN(lw) && lw > 0 ? lw : sw != null && !Number.isNaN(sw) && sw > 0 ? sw : null,
        height: lh != null && !Number.isNaN(lh) && lh > 0 ? lh : sh != null && !Number.isNaN(sh) && sh > 0 ? sh : null,
        size: resolveImageDisplayByteSize({ ...imageWithUrl, ...s }, effectiveSrc),
        url: trimmed(imageWithUrl.url) || trimmed(s.url) || imageWithUrl.url,
        viewUrl: trimmed(imageWithUrl.viewUrl) || trimmed(s.viewUrl) || trimmed(s.storedViewUrl) || '',
        displayUrl:
          trimmed(imageWithUrl.displayUrl)
          || trimmed(s.displayUrl)
          || trimmed(s.viewUrl)
          || trimmed(s.storedUrlDisplay)
          || '',
        dataUrl: imageWithUrl.dataUrl || s.dataUrl || null,
        format: imageWithUrl.format || s.format || '',
        source: imageWithUrl.source || s.source || 'upload',
        createdAt:
          (imageWithUrl.createdAt && String(imageWithUrl.createdAt).trim()) ||
          serverCreated ||
          imageWithUrl.createdAt ||
          '',
        usageCount:
          Number(imageWithUrl.usageCount ?? imageWithUrl.useCount ?? s.usageCount ?? s.useCount ?? 0) || 0,
        useCount:
          Number(imageWithUrl.useCount ?? imageWithUrl.usageCount ?? s.useCount ?? s.usageCount ?? 0) || 0,
        ownerId: imageWithUrl.ownerId || s.ownerId || s.owner_id || s.createdByUserId || s.userId || '',
        ownerName: imageWithUrl.ownerName || s.ownerName || s.owner_name || '',
        createdBy: imageWithUrl.createdBy || s.createdBy || s.created_by || s.creator || '',
        createdByName: imageWithUrl.createdByName || s.createdByName || s.created_by_name || '',
      };
    }
  } catch (err) {
    console.warn('预览合并服务端元数据失败', err);
  }

  return imageWithUrl;
}
